import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { assistantService, documentService } from "../../services/api";

// The content stays in its existing Outbox draft while its destination is reviewed.
export default function DocumentActionReview({ action, busy, run }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("");
  const [request, setRequest] = useState(null);
  const ref = action.payload.document_reference;
  const ready = ref?.receipt_version === 1;
  const review = useQuery({
    queryKey: ["document-action-review", action.id, ref?.receipt_key],
    queryFn: () => documentService.reviewAction(action.id),
    enabled: open && action.status === "draft",
    refetchOnWindowFocus: false,
  });
  const source = review.data?.source;
  const isDraft = action.status === "draft";
  async function bind() {
    const values = {
      source_id: source.id,
      expected_revision: source.revision,
      expected_action_revision: review.data.action_revision,
      tab_id: source.provider === "docs" ? tab : null,
    };
    const fingerprint = JSON.stringify(values);
    const saved = request?.fingerprint === fingerprint ? request : { fingerprint, id: crypto.randomUUID() };
    setRequest(saved);
    await documentService.bindAction(action.id, { ...values, request_id: saved.id });
    setOpen(false);
  }
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-[#ccd6cc] bg-[#f5f8f3] p-4 text-sm">
      {ready && (
        <p>
          Destination: <strong>{ref.tab_title || "Selected document"}</strong>.{" "}
          <Link className="underline" to={`/documents/${ref.source_id}`}>Review source document</Link>
        </p>
      )}
      {ready && action.kind === "notion_append" && (
        <div>
          <p>{isDraft ? "The following receipt link will be added after your notes:" : "Receipt link prepared with this append:"}</p>
          <a className="underline" href={ref.receipt_url}>{"Timelyzer update record"}</a>
          <p className="mt-1 text-xs text-[#66716a]">Notion edits made between the final check and append cannot be locked out.</p>
        </div>
      )}
      {ready && action.kind === "docs_append" && <p className="text-xs text-[#66716a]">Timelyzer uses a named range to verify this update. It adds no visible text.</p>}
      {isDraft && (
        <>
          {!ready && <p>Review the document and destination tab before applying this draft.</p>}
          <button className="secondary-button" disabled={busy} onClick={() => { setOpen(!open); setTab(""); }}>
            {open ? "Close destination review" : ready ? "Review destination again" : "Review append destination"}
          </button>
          {open && (
            <div className="space-y-3">
              {review.isFetching && <p role="status">Loading document snapshot…</p>}
              {review.error && <p role="alert">{review.error.response?.data?.detail || "Could not load the document. Try opening the review again."}</p>}
              {review.data && !source && <p>Connect and sync this document in <Link className="underline" to="/documents">Documents</Link>, then reopen this review.</p>}
              {source && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <a href={source.url} target="_blank" rel="noreferrer" className="font-semibold underline">{source.title}</a>
                    <button className="secondary-button" disabled={busy || review.isFetching} onClick={() => run(async () => {
                      await assistantService.sync(source.id);
                      setTab("");
                      await review.refetch();
                    })}>Sync latest document</button>
                  </div>
                  {source.error && <p role="alert">{source.error}</p>}
                  {source.object_type === "data_source" && <p>Select an individual Notion row page for an append; database properties are read-only here.</p>}
                  {(!source.snapshot?.revision || source.snapshot?.limited) && <p>Sync a complete snapshot before reviewing this destination.</p>}
                  <div className="max-h-64 space-y-3 overflow-y-auto rounded border bg-white p-3" aria-label="Document snapshot">
                    {(source.snapshot?.sections || []).map(section => (
                      <section key={section.key}>
                        {section.title && <h4 className="font-semibold">{section.title}</h4>}
                        <p className="whitespace-pre-wrap">{section.text}</p>
                      </section>
                    ))}
                  </div>
                  {source.provider === "docs" && (
                    <label className="block">Destination tab
                      <select className="mt-1 block w-full rounded border bg-white p-2" value={tab} onChange={e => setTab(e.target.value)}>
                        <option value="">Choose a tab</option>
                        {(source.snapshot?.sections || []).map(section => <option key={section.key} value={section.key}>{section.title || "Document body"}</option>)}
                      </select>
                    </label>
                  )}
                  <p>This saves the destination review. Apply the draft below to append the displayed notes{source.provider === "notion" ? " and its Timelyzer update record link" : ""}.</p>
                  <button className="primary-button" disabled={source.object_type === "data_source" || busy || review.isFetching || !!source.error || !source.snapshot?.revision || source.snapshot?.limited || (source.provider === "docs" && !tab)} onClick={() => run(bind)}>
                    Save reviewed destination
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
      {["sent", "sending", "needs_review"].includes(action.status) && !action.result?.verified && (
        <>
          <button disabled={busy} className="secondary-button" onClick={() => run(() => documentService.reconcileAction(action.id))}>Check document result</button>
          <p className="text-xs text-[#66716a]">This only reads the document. Missing or changed evidence leaves the result unverified; it never sends the draft again.</p>
        </>
      )}
      {action.result?.document_receipt?.checked_at && <p className="text-xs text-[#66716a]">Last checked: {new Date(action.result.document_receipt.checked_at).toLocaleString()}</p>}
    </div>
  );
}
